import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import Pagination from "../../components/ui/Pagination";
import {
  useAdjustBond,
  useReleaseBond,
  useTrainingBonds,
  useUploadBondCopy,
} from "../../api/queries/learningQueries";
import { useCanModify } from "../../hooks/usePermissions";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime, formatPlainDate } from "../../utils/dateTime";
import { safeMapUrl } from "../../utils/locationFields";
import {
  BOND_STATUS,
  bondStatus,
  canAdjustBond,
  canAttachSignedCopy,
  canReleaseBond,
  paybackRuleLabel,
  peso,
  signedCopyProblem,
} from "../../utils/trainingBond";
import BondDefaultsDialog from "./components/BondDefaultsDialog";
import "./learning.css";

const EMPTY = [];

/** One person's cost or months. It clears their acceptance. */
const AdjustDialog = ({ bond, onClose }) => {
  const [cost, setCost] = useState(String(bond.cost ?? ""));
  const [months, setMonths] = useState(String(bond.months ?? ""));
  const adjust = useAdjustBond();
  const monthsOk =
    Number.isInteger(Number(months)) &&
    Number(months) >= 1 &&
    Number(months) <= 120;
  const ok = Number(cost) > 0 && monthsOk;

  const save = () =>
    adjust.mutate(
      { id: bond.id, cost: Number(cost), months: Number(months) },
      {
        onSuccess: (res) => {
          toast.success(
            res?.acceptanceCleared
              ? "Updated. They must accept the agreement again."
              : "Updated.",
          );
          onClose();
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not update the bond.")),
      },
    );

  return (
    <Dialog
      title="Adjust this bond"
      subtitle={`${bond.employee.name} · ${bond.seminar.title}`}
      onClose={onClose}
      busy={adjust.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={adjust.isPending}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={save}
            disabled={adjust.isPending || !ok}
          >
            Save
          </button>
        </>
      }
    >
      {bond.acceptedAt && (
        <div className="alert alert-warning fs-085 py-2" role="alert">
          This clears their acceptance, so they must accept the agreement again.
        </div>
      )}
      <div className="row g-3">
        <div className="col-sm-6">
          <label htmlFor="bond-cost" className="profile-label">
            Cost (₱)
          </label>
          <input
            id="bond-cost"
            type="number"
            min="0.01"
            step="0.01"
            className="form-control"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
          />
        </div>
        <div className="col-sm-6">
          <label htmlFor="bond-months" className="profile-label">
            Months
          </label>
          <input
            id="bond-months"
            type="number"
            min="1"
            max="120"
            step="1"
            className="form-control"
            value={months}
            onChange={(e) => setMonths(e.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
};

/** Waive a bond: nothing is owed. Needs a reason. */
const ReleaseDialog = ({ bond, onClose }) => {
  const [reason, setReason] = useState("");
  const release = useReleaseBond();
  const save = () =>
    release.mutate(
      { id: bond.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success("Bond released: nothing is owed.");
          onClose();
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not release the bond.")),
      },
    );
  return (
    <Dialog
      title="Release this bond?"
      subtitle={`${bond.employee.name} · ${bond.seminar.title}`}
      onClose={onClose}
      busy={release.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={release.isPending}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-danger profile-btn"
            onClick={save}
            disabled={release.isPending || !reason.trim()}
          >
            Release bond
          </button>
        </>
      }
    >
      <p className="fs-085">
        Nothing will be owed on this bond, and this can't be undone here.
      </p>
      <label htmlFor="bond-reason" className="profile-label">
        Reason <span className="text-danger">*</span>
      </label>
      <textarea
        id="bond-reason"
        className="form-control"
        rows={3}
        maxLength={500}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        autoFocus
      />
    </Dialog>
  );
};

/**
 * Learning → Training Bonds: everyone with a training agreement, where each
 * bond stands and what is owed. Reference only: no pay deductions.
 */
const TrainingBondsPage = () => {
  const canModify = useCanModify("learning", { company: true });
  const [params, setParams] = useSearchParams();
  const seminarId = params.get("seminarId") || "";
  const [status, setStatus] = useState("");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [adjusting, setAdjusting] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const fileRef = useRef(null);
  const uploadFor = useRef(null);
  const upload = useUploadBondCopy();

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);
  useEffect(() => setPage(1), [status, search, seminarId]);

  const query = useTrainingBonds({ status, search, seminarId, page, limit });
  const rows = query.data?.data || EMPTY;
  const totals = query.data?.totals || {};

  const pickFile = (bond) => {
    uploadFor.current = bond;
    fileRef.current?.click();
  };
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadFor.current) return;
    const problem = signedCopyProblem(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    upload.mutate(
      { id: uploadFor.current.id, file },
      {
        onSuccess: () => toast.success("Signed copy saved."),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not upload the file.")),
      },
    );
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Training Bonds</h1>
            <p className="text-muted small mb-0">
              Who agreed to stay after a training, until when, and what is owed.
            </p>
          </div>
          {canModify && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setDefaultsOpen(true)}
            >
              <i className="bi bi-sliders me-1"></i>Payback defaults
            </button>
          )}
        </div>

        <div className="alert alert-info fs-085 py-2" role="note">
          <i className="bi bi-info-circle me-1"></i>
          Amounts are for reference. Nothing is deducted from pay automatically.
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span className="status-pill status-pill--green">
            Active: {totals.active ?? 0}
          </span>
          <span className="status-pill status-pill--red">
            Left during bond: {totals.separated ?? 0}
          </span>
          <span className="status-pill status-pill--amber">
            Owed by those who left: {peso(totals.owed ?? 0)}
          </span>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <select
            className="status-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Status"
          >
            <option value="">All statuses</option>
            {Object.entries(BOND_STATUS).map(([key, s]) => (
              <option key={key} value={key}>
                {s.label}
              </option>
            ))}
          </select>
          <input
            type="search"
            className="filter-input flex-grow-1"
            placeholder="Employee, number or training"
            aria-label="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {seminarId && (
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600"
              onClick={() => setParams({}, { replace: true })}
            >
              Show all trainings
            </button>
          )}
        </div>

        <input
          ref={fileRef}
          type="file"
          className="d-none"
          accept=".pdf,image/*"
          onChange={onFile}
          aria-label="Signed copy"
        />

        <div className="glass-table-card">
          {query.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(query.error, "Could not load the bonds.")}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table mb-0 learn-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th className="ps-4 py-3">Employee</th>
                      <th className="py-3">Training</th>
                      <th className="py-3">Status</th>
                      <th className="py-3 text-end">Cost</th>
                      <th className="py-3">Term</th>
                      <th className="py-3">Accepted</th>
                      <th className="py-3 text-end">Owed</th>
                      <th className="py-3">Signed copy</th>
                      <th className="py-3 pe-4 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {rows.map((b) => {
                      const meta = bondStatus(b.status);
                      const copyUrl = safeMapUrl(b.signedCopy?.url);
                      return (
                        <tr
                          key={b.id}
                          className={
                            b.status === "SEPARATED"
                              ? "learn-row-alert"
                              : undefined
                          }
                        >
                          <td className="ps-4">
                            <div className="emp-name-text">
                              {b.employee.name}
                            </div>
                            <div className="emp-sub-text">
                              {b.employee.employeeNumber}
                            </div>
                          </td>
                          <td className="fs-080">
                            <Link to={`/learning/seminars/${b.seminar.id}`}>
                              {b.seminar.title}
                            </Link>
                            <div className="text-slate-500 fs-075">
                              {formatPlainDate(b.seminar.endAt)}
                              {b.seminar.branchName
                                ? ` · ${b.seminar.branchName}`
                                : ""}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`status-pill status-pill--${meta.tone}`}
                            >
                              {meta.label}
                            </span>
                            {b.separatedOn && (
                              <div className="fs-070 text-slate-500">
                                Left {formatPlainDate(b.separatedOn)}
                              </div>
                            )}
                            {b.releaseReason && (
                              <div
                                className="fs-070 text-slate-500"
                                title={b.releaseReason}
                              >
                                {b.releaseReason}
                              </div>
                            )}
                          </td>
                          <td className="text-end tabular-nums text-nowrap">
                            {peso(b.cost)}
                            {b.adjusted && (
                              <div className="fs-070 text-slate-500">
                                adjusted
                              </div>
                            )}
                          </td>
                          <td className="fs-080">
                            {b.months} months ·{" "}
                            {paybackRuleLabel(b.paybackRule)}
                            {b.startsOn && (
                              <div className="text-slate-500 fs-075 text-nowrap">
                                {formatPlainDate(b.startsOn)} –{" "}
                                {formatPlainDate(b.endsOn)}
                              </div>
                            )}
                          </td>
                          <td className="fs-080 text-nowrap">
                            {b.acceptedAt ? formatDateTime(b.acceptedAt) : "—"}
                          </td>
                          <td
                            className={`text-end tabular-nums text-nowrap ${b.status === "SEPARATED" ? "fw-bold text-danger" : ""}`}
                          >
                            {b.status === "ACTIVE" || b.status === "SEPARATED"
                              ? peso(b.balance)
                              : "—"}
                          </td>
                          <td className="fs-080">
                            {copyUrl ? (
                              <a
                                href={copyUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="bi bi-file-earmark-text me-1"></i>
                                {b.signedCopy.fileName || "View"}
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="pe-4 text-end text-nowrap">
                            {canModify && (
                              <div className="d-inline-flex gap-1">
                                {canAttachSignedCopy(b.status) && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-light border"
                                    onClick={() => pickFile(b)}
                                    disabled={upload.isPending}
                                    title={
                                      b.signedCopy
                                        ? "Replace the signed copy"
                                        : "Upload the signed copy (PDF or image, 10 MB)"
                                    }
                                    aria-label={`Upload signed copy for ${b.employee.name}`}
                                  >
                                    <i className="bi bi-upload"></i>
                                  </button>
                                )}
                                {canAdjustBond(b.status) && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-light border"
                                    onClick={() => setAdjusting(b)}
                                  >
                                    Adjust
                                  </button>
                                )}
                                {canReleaseBond(b.status) && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-light border text-danger"
                                    onClick={() => setReleasing(b)}
                                  >
                                    Release
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!query.isLoading && rows.length === 0 && (
                      <tr>
                        <td colSpan={9} className="approval-empty">
                          <i className="bi bi-file-earmark-text"></i>No training
                          bonds here.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {query.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
              <Pagination
                page={page}
                limit={limit}
                total={query.data?.total || 0}
                onPageChange={setPage}
                onLimitChange={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
              />
            </>
          )}
        </div>
      </div>

      {adjusting && (
        <AdjustDialog bond={adjusting} onClose={() => setAdjusting(null)} />
      )}
      {releasing && (
        <ReleaseDialog bond={releasing} onClose={() => setReleasing(null)} />
      )}
      {defaultsOpen && (
        <BondDefaultsDialog onClose={() => setDefaultsOpen(false)} />
      )}
    </Layout>
  );
};

export default TrainingBondsPage;
